import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api, formatApiError } from '../auth/AuthContext';

const CLASS_OPTIONS = ['Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
const BRANCH_OPTIONS = ['Behala, Kolkata', 'Howrah'];
const EMPTY_FORM = { studentName: '', parentName: '', classApplyingFor: '', branch: '', phone: '', email: '', city: '' };

function SuccessCard({ name, phone, onReset }) {
  return (
    <div className="bg-white rounded-2xl border border-orange-100 p-8 text-center shadow-coral" data-testid="admission-success-card">
      <div className="w-14 h-14 rounded-full bg-coral-light text-coral flex items-center justify-center mx-auto mb-4">
        <Check className="w-7 h-7" />
      </div>
      <h3 className="font-serif text-2xl font-semibold mb-2">Thank you, {name.split(' ')[0]}!</h3>
      <p className="text-stone-600 max-w-sm mx-auto">Your admission enquiry has been received. Our team will call you on <strong>{phone}</strong> within 24 hours.</p>
      <button onClick={onReset} className="mt-6 text-coral font-medium hover:underline">Submit another</button>
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AdmissionForm({ compact = false }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.studentName || !form.phone || !form.classApplyingFor || !form.branch) {
      toast({ title: 'Please complete required fields', description: 'Student name, phone, class and branch are required.' });
      return;
    }
    setBusy(true);
    try {
      await api.post('/leads', form);
      setSubmitted(true);
      toast({ title: 'Admission form received', description: 'Our team will contact you within 24 hours.' });
    } catch (err) {
      toast({ title: 'Submission failed', description: formatApiError(err?.response?.data?.detail, 'Please try again or call us directly.') });
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return <SuccessCard name={form.studentName} phone={form.phone} onReset={() => { setSubmitted(false); setForm(EMPTY_FORM); }} />;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-orange-100 p-6 sm:p-8 shadow-coral" data-testid="admission-form">
      <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-4`}>
        <div className="space-y-1.5">
          <Label htmlFor="studentName">Student Name *</Label>
          <Input id="studentName" value={form.studentName} onChange={(e) => update('studentName', e.target.value)} placeholder="Full name" data-testid="admission-student-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="parentName">Parent / Guardian Name</Label>
          <Input id="parentName" value={form.parentName} onChange={(e) => update('parentName', e.target.value)} placeholder="Full name" />
        </div>

        <SelectField label="Class Applying For *" value={form.classApplyingFor} onChange={(v) => update('classApplyingFor', v)} options={CLASS_OPTIONS} placeholder="Select class" testId="admission-class-select" />
        <SelectField label="Preferred Branch *" value={form.branch} onChange={(v) => update('branch', v)} options={BRANCH_OPTIONS} placeholder="Select branch" testId="admission-branch-select" />

        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input id="phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="10-digit mobile" data-testid="admission-phone" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="name@email.com" />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="city">City</Label>
          <Input id="city" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Kolkata / Howrah / Other" />
        </div>
      </div>
      <Button type="submit" disabled={busy} data-testid="admission-submit-btn" className="w-full mt-6 btn-primary border-0 h-11 text-[0.95rem] font-medium">
        {busy ? 'Submitting...' : 'Submit Admission Form'}
      </Button>
      <p className="text-xs text-stone-500 text-center mt-3">We'll call you within 24 hours. Your details stay confidential.</p>
    </form>
  );
}
